import { useLoaderData } from 'react-router';

import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { SectionSpacer } from '~/ui-rocco/pages/home-page/components/SectionSpacer';
import { DetailScrollReveal } from '~/ui-rocco/pages/detail-shared';
import { ProfessionalOverviewAccordionSection } from './sections/ProfessionalOverviewAccordionSection';
import { ProfessionalAboutSection } from './sections/ProfessionalAboutSection';
import { ProfessionalSelectedWorkSection } from './sections/ProfessionalSelectedWorkSection';
import { ProfessionalTeamSection } from './sections/ProfessionalTeamSection';
import { ProfessionalNetworkSection } from './sections/ProfessionalNetworkSection';
import { ProfessionalArticlesSection } from './sections/ProfessionalArticlesSection';
import { ProfessionalContactSection } from './sections/ProfessionalContactSection';
import { hasAuthoredFeature } from './utils/about-feature';
import { getSelectedShowcase } from './utils/featured-work';
import { getOverviewSectionNumbers } from './utils/section-numbering';
import { useProfileFamily } from '@modules/professional/hook/use-profile-family';

/**
 * BC-4505 — revamped editorial professional details page.
 *
 * Built section-by-section per `plans/BC-4505-details-page-revamp.md`, composed
 * from the shared `detail-shared/` library + professional-specific pieces.
 * Rendered in place of the legacy overview page behind the
 * `revamp_2026` feature flag (or `?revamp=1` in local dev).
 *
 * Pending data wiring (loader aggregation / codegen): (02) Team, (04) Articles,
 * the project-value stat, and the (03) Who-we-work-with partner block.
 */
export const ProfessionalPageRevamp = () => {
    const {
        professionalOverview,
        featuredProjects,
        featuredProducts,
        teamMembers,
        networkProfessionals,
        articleTiles,
    } = useLoaderData<typeof loader>();

    // Section "(0N)" numbers run as one continuous, gap-free sequence down the
    // page; each `has*` here mirrors the matching section's own self-hide guard.
    // Book a Meeting is deliberately absent: it is a docked CTA that opens the
    // gate, not an accordion row, so it takes no number and adds no row.
    // "Explore our Brand Family" is an accordion row, so it counts towards the
    // accordion's own rows rather than the page's numbered sections.
    const hasFamily = useProfileFamily() !== null;
    // The Overview accordion self-hides when it has no rows; render its leading
    // spacer with it so we don't leave a double gap above About in that case.
    // Brand Family is now its only row, so the two conditions are the same one.
    const showOverviewAccordion = hasFamily;

    // Each optional section pairs its leading spacer with itself (below), so an
    // empty section drops its gap too — otherwise two spacers stack into a
    // double gap above the next section.
    const hasTeam = (teamMembers ?? []).length > 0;
    const hasNetwork = (networkProfessionals ?? []).length > 0;
    const hasArticles = (articleTiles ?? []).length > 0;

    // The About band is desktop-only unless the brand authored its own media,
    // which changes what leads the page on mobile.
    const hasAuthoredAboutFeature = hasAuthoredFeature(professionalOverview);

    const hasSelectedWork =
        getSelectedShowcase(
            featuredProjects.featuredProjectsList,
            featuredProducts.featuredProductsList,
            Boolean(professionalOverview.IsManufacturer),
        ).items.length > 0;
    const numbers = getOverviewSectionNumbers({
        hasFamily,
        hasSelectedWork,
        hasTeam,
        hasNetwork,
        hasArticles,
    });

    return (
        <main
            id="main-content"
            data-testid="professional-page-revamp"
            className="bg-ground ground-grey max-md:pb-16 md:pb-[6.25rem]"
        >
            {/* Sub-nav → first content. Every tab's first block starts at this
                offset (see TAB_CONTENT_GAP), so the heading + CTA cluster never
                jumps between tabs. The tab band carries 24px of its own below
                the pills, so the gap reads as 48px. */}
            <SectionSpacer
                height={24}
                mobileHeight={24}
                unit="rem"
                className="bg-ground"
            />
            {/* The services and locations that opened the overview here on
                the phone now sit on the About tab, under the practice. */}
            {/* Nothing of its own: the summary closes on 24 and the band
                below opens on 24, which is the 48 between the practice's
                details and the first image.

                This used to be up to 170px, set off the accordion's last row —
                but the accordion closes the tab now rather than opening it, so
                there is no last row here to space off and that gap was left
                stranded between the details and the image.

                On mobile it leads whichever section actually renders: an
                authored About band or Selected work take the inter-section
                tier; with neither there is nothing after it and the gap would
                strand against the next section's own spacer. */}
            <SectionSpacer
                height={0}
                mobileHeight={
                    hasAuthoredAboutFeature || hasSelectedWork ? 24 : 0
                }
                className="bg-ground"
                unit="rem"
            />
            {/* About and Selected work share one continuous dark background
                when both render; either can also stand alone. */}
            <ProfessionalAboutSection hasSelectedWork={hasSelectedWork} />
            <ProfessionalSelectedWorkSection
                sectionNumber={numbers.selectedWork}
            />
            {hasTeam && (
                <>
                    {/* 100 here + the editorial header's own 60 makes the white
                        run into Team match the 160 of black below Selected
                        work's CTA. */}
                    <SectionSpacer
                        height={100}
                        mobileHeight={40}
                        className="bg-ground"
                        unit="rem"
                    />
                    <DetailScrollReveal>
                        <ProfessionalTeamSection />
                    </DetailScrollReveal>
                </>
            )}
            {hasNetwork && (
                <>
                    <SectionSpacer
                        height={70}
                        mobileHeight={20}
                        className="bg-ground"
                        unit="rem"
                    />
                    <DetailScrollReveal>
                        <ProfessionalNetworkSection
                            sectionNumber={numbers.network}
                        />
                    </DetailScrollReveal>
                </>
            )}
            {hasArticles && (
                <>
                    <SectionSpacer
                        height={160}
                        mobileHeight={40}
                        className="bg-ground"
                        unit="rem"
                    />
                    <DetailScrollReveal>
                        <ProfessionalArticlesSection />
                    </DetailScrollReveal>
                </>
            )}
            <ProfessionalContactSection />
            {/* Book a Meeting closes the tab rather than opening it.
                Near the top it asked for a meeting before the reader had seen
                any of the work; at the foot it follows all of it, and the
                reader who has read the whole page is the one worth asking. */}
            {showOverviewAccordion && (
                <>
                    <SectionSpacer
                        height={100}
                        mobileHeight={36}
                        unit="rem"
                        className="bg-ground"
                    />
                    <ProfessionalOverviewAccordionSection />
                </>
            )}
        </main>
    );
};
